// AdminAlumnusTableDialog.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Button,
  Chip,
  Divider,
  Paper,
  Typography,
  TextField,
  Stack,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemSecondaryAction,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import FolderIcon from "@mui/icons-material/Folder";
import TemplateIcon from "@mui/icons-material/Description";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

import SmsInvitationManager from "./SmsInvitationManager";
import { API_ENDPOINTS } from "config/api";
import safeToast from "utils/safeToast";
import axiosInstance from "API/axiosInstance";

// Register AG Grid modules ONCE
ModuleRegistry.registerModules([AllCommunityModule]);

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const AdminAlumnusTableDialog = ({ open, onClose }) => {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customMode, setCustomMode] = useState(false);
  const [smsMode, setSmsMode] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]); // array of field keys
  const [reportTitle, setReportTitle] = useState("Custom Report");
  const [reportSubtitle, setReportSubtitle] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  
  // Template management state
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savedTemplates, setSavedTemplates] = useState([]);

  // Simple cell renderer to show full value on hover, keep ellipsis in cell
  const textCellRenderer = useCallback((params) => {
    const value = params.value ?? "";
    return (
      <span title={value} style={{ display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value}
      </span>
    );
  }, []);

  // Define columns
  const [columnDefs] = useState([
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 60,
      minWidth: 50,
      maxWidth: 70,
      resizable: false,
      sortable: false,
      filter: false,
      pinned: "left",
      suppressSizeToFit: true,
      suppressMenu: true,
      cellStyle: { textAlign: "center" },
    },
    { field: "fullName", headerName: "Full Name", sortable: true, filter: true, flex: 2, cellRenderer: textCellRenderer },
    { field: "rollNum", headerName: "Roll Number", sortable: true, filter: true, flex: 1, cellRenderer: textCellRenderer },
    { field: "customGender", headerName: "Gender", sortable: true, filter: true, flex: 1 },
    { field: "customBatch", headerName: "Batch", sortable: true, filter: true, flex: 1 },
    { field: "customCollegeStudied", headerName: "College", sortable: true, filter: true, flex: 2, cellRenderer: textCellRenderer },
    { field: "customCourseStudied", headerName: "Course", sortable: true, filter: true, flex: 1.5, cellRenderer: textCellRenderer },
    { field: "customBranch", headerName: "Branch", sortable: true, filter: true, flex: 1, cellRenderer: textCellRenderer },
    { field: "customWhatsappNumber", headerName: "Whatsapp", sortable: true, filter: true, flex: 1.5, cellRenderer: textCellRenderer },
    { field: "customPresentStatus", headerName: "Status", sortable: true, filter: true, flex: 1.5, cellRenderer: textCellRenderer },
    { field: "customOrganization", headerName: "Organization", sortable: true, filter: true, flex: 2, cellRenderer: textCellRenderer },
    { field: "customDesignation", headerName: "Designation", sortable: true, filter: true, flex: 1.5, cellRenderer: textCellRenderer },
    { field: "customAvailability", headerName: "Availability", sortable: true, filter: true, flex: 1, cellRenderer: textCellRenderer },
    { field: "customNativePlace", headerName: "Native Place", sortable: true, filter: true, flex: 1.5, cellRenderer: textCellRenderer },
  ]);

  // Available field meta for custom builder
  const availableFields = useMemo(() => {
    return columnDefs
      .filter((c) => !!c.field) // omit S.No synthetic column
      .map((c) => ({ field: c.field, headerName: c.headerName || c.field }));
  }, [columnDefs]);

  // Template management functions
  const TEMPLATES_STORAGE_KEY = 'alumni_report_templates';

  // Load templates from localStorage on component mount
  useEffect(() => {
    const loadTemplates = () => {
      try {
        const stored = localStorage.getItem(TEMPLATES_STORAGE_KEY);
        if (stored) {
          setSavedTemplates(JSON.parse(stored));
        }
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    };
    loadTemplates();
  }, []);

  // Save templates to localStorage
  const saveTemplatesToStorage = useCallback((templates) => {
    try {
      localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
    } catch (error) {
      console.error('Error saving templates:', error);
    }
  }, []);

  // Save current configuration as template
  const saveCurrentAsTemplate = useCallback(() => {
    if (!templateName.trim()) return;
    
    const newTemplate = {
      id: Date.now().toString(),
      name: templateName.trim(),
      selectedFields: [...selectedFields],
      reportTitle,
      reportSubtitle,
      logoDataUrl,
      createdAt: new Date().toISOString(),
    };
    
    const updatedTemplates = [...savedTemplates, newTemplate];
    setSavedTemplates(updatedTemplates);
    saveTemplatesToStorage(updatedTemplates);
    
    setTemplateName('');
    setSaveTemplateDialogOpen(false);
  }, [templateName, selectedFields, reportTitle, reportSubtitle, logoDataUrl, savedTemplates, saveTemplatesToStorage]);

  // Load template
  const loadTemplate = useCallback((template) => {
    setSelectedFields(template.selectedFields || []);
    setReportTitle(template.reportTitle || 'Custom Report');
    setReportSubtitle(template.reportSubtitle || '');
    setLogoDataUrl(template.logoDataUrl || null);
    setTemplateManagerOpen(false);
  }, []);

  // Delete template
  const deleteTemplate = useCallback((templateId) => {
    const updatedTemplates = savedTemplates.filter(t => t.id !== templateId);
    setSavedTemplates(updatedTemplates);
    saveTemplatesToStorage(updatedTemplates);
  }, [savedTemplates, saveTemplatesToStorage]);

  // Grid defaults for a professional feel
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      floatingFilter: true,
      minWidth: 120,
    }),
    []
  );

  // Fetch data
  const fetchAlumnusData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_ALUMNUS_DETAILS);
      setRowData(response.data);
    } catch (error) {
      console.error("Error fetching alumnus data:", error);
      safeToast.error("Failed to fetch alumnus data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchAlumnusData();
    }
  }, [open, fetchAlumnusData]);

  // No custom toolbar filters – use column filters only

  // Grid helpers
  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
    // Fit columns on first render
    params.api.sizeColumnsToFit({ defaultMinWidth: 120 });
  }, []);

  // Custom builder helpers
  const handleToggleMode = useCallback(() => {
    setCustomMode((v) => !v);
  }, []);

  const handleAddField = useCallback((field) => {
    setSelectedFields((prev) => (prev.includes(field) ? prev : [...prev, field]));
  }, []);

  const handleRemoveField = useCallback((field) => {
    setSelectedFields((prev) => prev.filter((f) => f !== field));
  }, []);

  const moveField = useCallback((field, direction) => {
    setSelectedFields((prev) => {
      const index = prev.indexOf(field);
      if (index === -1) return prev;
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const clone = [...prev];
      const [item] = clone.splice(index, 1);
      clone.splice(target, 0, item);
      return clone;
    });
  }, []);

  // DnD: available -> workspace (append)
  const onDragStartAvailable = useCallback((e, field) => {
    e.dataTransfer.setData("app/field", field);
    e.dataTransfer.effectAllowed = "copy";
  }, []);

  const onDragOverWorkspace = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const onDropWorkspace = useCallback((e) => {
    e.preventDefault();
    const field = e.dataTransfer.getData("app/field");
    if (field) {
      handleAddField(field);
    }
  }, [handleAddField]);

  const fieldLabel = useCallback((field) => {
    const m = availableFields.find((x) => x.field === field);
    return m?.headerName || field;
  }, [availableFields]);

  // Debug: Log data structure when fields change
  useEffect(() => {
    if (selectedFields.length > 0 && rowData.length > 0) {
      console.log('Selected fields:', selectedFields);
      console.log('Sample row data:', rowData[0]);
      console.log('Available fields:', availableFields);
    }
  }, [selectedFields, rowData, availableFields]);

  // Branding: logo upload
  const onLogoSelected = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoDataUrl(reader.result);
    reader.readAsDataURL(file);
  }, []);

  // Export CSV for preview
  const exportCsv = useCallback(() => {
    if (selectedFields.length === 0 || rowData.length === 0) return;
    const headerLine = selectedFields
      .map((f) => '"' + (fieldLabel(f) || f).replace(/\"/g, '""') + '"')
      .join(",");
    const lines = rowData.map((row) =>
      selectedFields
        .map((f) => {
          const v = row[f] == null ? "" : String(row[f]);
          return '"' + v.replace(/\"/g, '""') + '"';
        })
        .join(",")
    );
    const csv = [headerLine, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.download = `${reportTitle || "report"}-${ts}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedFields, rowData, fieldLabel, reportTitle]);

  // Export PDF
  const exportPdf = useCallback(async () => {
    if (selectedFields.length === 0 || rowData.length === 0) return;

    try {
      // Dynamic import for jsPDF + autotable (handle different export shapes)
      const { jsPDF } = await import('jspdf');
      const autoTableModule = await import('jspdf-autotable');
      const autoTable = autoTableModule.default || autoTableModule.autoTable || autoTableModule;

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPosition = 20;

      // Add logo if available
      if (logoDataUrl) {
        try {
          const img = new Image();
          img.src = logoDataUrl;
          await new Promise((resolve) => {
            img.onload = () => {
              const imgWidth = 30;
              const imgHeight = (img.height * imgWidth) / img.width;
              const format = logoDataUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
              doc.addImage(logoDataUrl, format, 20, yPosition, imgWidth, imgHeight);
              yPosition += imgHeight + 10;
              resolve();
            };
          });
        } catch (error) {
          console.warn('Could not add logo to PDF:', error);
        }
      }

      // Add title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(reportTitle || 'Custom Report', 20, yPosition);
      yPosition += 10;

      // Add subtitle if available
      if (reportSubtitle) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(reportSubtitle, 20, yPosition);
        yPosition += 15;
      }

      // Prepare table data
      const headers = selectedFields.map(f => fieldLabel(f));
      const data = rowData.map(row =>
        selectedFields.map(f => row[f] !== undefined && row[f] !== null ? String(row[f]) : '')
      );

      // Add table
      autoTable(doc, {
        head: [headers],
        body: data,
        startY: yPosition,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [66, 139, 202] },
        margin: { left: 14, right: 14 },
        tableWidth: 'wrap',
        // prevent overflow
        didDrawPage: (dataCtx) => {
          // If table exceeds page, jsPDF-Autotable handles page breaks automatically
        },
      });

      // Save PDF
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      doc.save(`${reportTitle || "report"}-${ts}.pdf`);
    } catch (error) {
      console.error('PDF export error:', error);
      safeToast.error('PDF export failed. Please try again.');
    }
  }, [selectedFields, rowData, fieldLabel, reportTitle, reportSubtitle, logoDataUrl]);

  // Export Word/DOCX
  const exportWord = useCallback(async () => {
    if (selectedFields.length === 0 || rowData.length === 0) return;

    try {
      // Dynamic import for docx; support both ESM/CJS shapes
      const docxModule = await import('docx');
      const {
        Document,
        Packer,
        Paragraph,
        Table,
        TableRow,
        TableCell,
        TextRun,
        HeadingLevel,
        AlignmentType,
        WidthType,
      } = docxModule.default || docxModule;

      // Create document
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Title
            new Paragraph({
              children: [
                new TextRun({
                  text: reportTitle || 'Custom Report',
                  bold: true,
                  size: 32,
                }),
              ],
              heading: HeadingLevel.TITLE,
              alignment: AlignmentType.CENTER,
            }),

            // Subtitle
            ...(reportSubtitle ? [new Paragraph({
              children: [
                new TextRun({
                  text: reportSubtitle,
                  size: 24,
                }),
              ],
              alignment: AlignmentType.CENTER,
            })] : []),

            // Table
            new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              rows: [
                // Header row
                new TableRow({
                  children: selectedFields.map(field =>
                    new TableCell({
                      children: [new Paragraph({
                        children: [new TextRun({
                          text: fieldLabel(field),
                          bold: true,
                        })],
                      })],
                    })
                  ),
                }),
                // Data rows
                ...rowData.map(row =>
                  new TableRow({
                    children: selectedFields.map(field =>
                      new TableCell({
                        children: [new Paragraph({
                          children: [new TextRun({
                            text: row[field] !== undefined && row[field] !== null ? String(row[field]) : '',
                          })],
                        })],
                      })
                    ),
                  })
                ),
              ],
            }),
          ],
        }],
      });

      // Generate and download using toBlob (works reliably in browser)
      const blob = await (Packer.toBlob ? Packer.toBlob(doc) : (async () => {
        const buffer = await Packer.toBuffer(doc);
        return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      })());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.download = `${reportTitle || 'report'}-${ts}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Word export error:', error);
      safeToast.error('Word export failed. Please try again.');
    }
  }, [selectedFields, rowData, fieldLabel, reportTitle, reportSubtitle]);

  // Reset functions
  const handleResetAll = useCallback(() => {
    setResetDialogOpen(true);
  }, []);

  const resetEverything = useCallback(() => {
    setLogoDataUrl(null);
    setReportTitle("Custom Report");
    setReportSubtitle("");
    setSelectedFields([]);
    setResetDialogOpen(false);
  }, []);

  const resetFieldsOnly = useCallback(() => {
    setSelectedFields([]);
    setResetDialogOpen(false);
  }, []);

  // SMS functions
  const handleToggleSmsMode = useCallback(() => {
    setSmsMode((v) => !v);
    setCustomMode(false);
  }, []);


  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {/* Title + Close Button */}
      <DialogTitle sx={{ pr: 8, fontWeight: "bold", color: "primary.main" }}>
        All Alumni
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <Tooltip title="Close">
            <IconButton onClick={onClose} color="error">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Header actions */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1 }}>
              <Typography variant="h6">
                {smsMode ? "SMS Invitation Manager" : customMode ? "Custom Report Builder" : "Alumni Grid"}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {customMode && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<SaveIcon />}
                      onClick={() => setSaveTemplateDialogOpen(true)}
                      disabled={selectedFields.length === 0}
                    >
                      Save Template
                    </Button>
                    <Button
                      color="primary"
                      startIcon={<FolderIcon />}
                      onClick={() => setTemplateManagerOpen(true)}
                    >
                      Templates ({savedTemplates.length})
                    </Button>
                    <Divider orientation="vertical" flexItem />
                    <Button
                      color="primary"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportCsv}
                      disabled={selectedFields.length === 0 || rowData.length === 0}
                    >
                      CSV
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={exportPdf}
                      disabled={selectedFields.length === 0 || rowData.length === 0}
                    >
                      PDF
                    </Button>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<DescriptionIcon />}
                      onClick={exportWord}
                      disabled={selectedFields.length === 0 || rowData.length === 0}
                    >
                      Word
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<RefreshIcon />}
                      onClick={handleResetAll}
                    >
                      Reset All
                    </Button>
                  </>
                )}
                <Button
                  variant={smsMode}
                  color="success"
                  onClick={handleToggleSmsMode}
                >
                  SMS Invitations
                </Button>
                <Button color="primary" onClick={handleToggleMode}>
                  Custom Report
                </Button>
              </Box>
            </Box>

            {!customMode && !smsMode && (
              <div className="ag-theme-alpine" style={{ height: "79vh", width: "99%" }}>
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowSelection="multiple"
                  animateRows={true}
                  pagination={true}
                  paginationPageSize={18}
                  onGridReady={onGridReady}
                  suppressRowClickSelection={true}
                />
              </div>
            )}

            {smsMode && (
              <SmsInvitationManager rowData={rowData} />
            )}

            {customMode && (
              <Box sx={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 2, p: 2, height: "88vh", boxSizing: "border-box" }}>
                {/* Left: Available headers */}
                <Paper variant="outlined" sx={{ p: 1.5, overflow: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ px: 0.5, pb: 1, fontSize: '0.7rem', fontWeight: 600 }}>Branding</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button component="label" size="small">
                        Upload Logo
                        <input type="file" hidden accept="image/*" onChange={onLogoSelected} />
                      </Button>
                      {logoDataUrl && (
                        <Box sx={{ border: 1, borderColor: "divider", p: 0.5, borderRadius: 1 }}>
                          <img src={logoDataUrl} alt="logo" style={{ height: 36, display: "block" }} />
                        </Box>
                      )}
                      {logoDataUrl && (
                        <Button size="small" color="error" onClick={() => setLogoDataUrl(null)}>
                          Remove
                        </Button>
                      )}
                    </Stack>
                    {/* Report Title/Subtitle under logo */}
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      <TextField size="small" label="Title" value={reportTitle} onChange={(e) => setReportTitle(e.target.value)} sx={{ '& .MuiInputLabel-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { fontSize: '0.7rem' } }} />
                      <TextField size="small" label="Subtitle" value={reportSubtitle} onChange={(e) => setReportSubtitle(e.target.value)} sx={{ '& .MuiInputLabel-root': { fontSize: '0.7rem' }, '& .MuiInputBase-input': { fontSize: '0.7rem' } }} />
                    </Stack>
                  </Box>
                  <Divider />

                  <Typography variant="caption" sx={{ px: 0.5, fontSize: '0.7rem', fontWeight: 600 }}>Available Fields</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0, mt: 0.5 }}>
                    {availableFields.map((f) => (
                      <FormControlLabel
                        key={f.field}
                        sx={{ m: 0, px: 0.5, minHeight: 28 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedFields.includes(f.field)}
                            onChange={(e) => {
                              if (e.target.checked) handleAddField(f.field);
                              else handleRemoveField(f.field);
                            }}
                          />
                        }
                        label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{f.headerName}</Typography>}
                        onDragStart={(e) => onDragStartAvailable(e, f.field)}
                        draggable
                      />
                    ))}
                  </Box>
                </Paper>

                {/* Right: Workspace + Preview */}
                <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, height: "100%" }}>
                  {/* Workspace (drop zone) */}
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, mb: 2, borderStyle: "dashed", borderColor: "divider", minHeight: 96 }}
                    onDragOver={onDragOverWorkspace}
                    onDrop={onDropWorkspace}
                  >
                    <Typography variant="caption" sx={{ mb: 1, fontSize: '0.7rem', fontWeight: 600 }}>Selected Fields (Drag here or click from left)</Typography>
                    {selectedFields.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Drop fields here to build your report.</Typography>
                    ) : (
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {selectedFields.map((sf) => (
                          <Box key={sf} sx={{ display: "flex", alignItems: "center", border: 1, borderColor: "divider", borderRadius: 1, pl: 1 }}>
                            <Typography variant="caption" sx={{ mr: 1, fontSize: '0.7rem' }}>{fieldLabel(sf)}</Typography>
                            <IconButton size="small" onClick={() => moveField(sf, "up")}><ArrowUpwardIcon fontSize="inherit" /></IconButton>
                            <IconButton size="small" onClick={() => moveField(sf, "down")}><ArrowDownwardIcon fontSize="inherit" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => handleRemoveField(sf)}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>

                  {/* Preview */}
                  <Paper variant="outlined" sx={{ p: 2, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                    {/* Report header */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      {logoDataUrl && (
                        <Box sx={{ mr: 1, border: 1, borderColor: "divider", p: 0.5, borderRadius: 1 }}>
                          <img src={logoDataUrl} alt="logo" style={{ height: 40 }} />
                        </Box>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" noWrap>{reportTitle || "Custom Report"}</Typography>
                        {reportSubtitle && (
                          <Typography variant="body2" color="text.secondary" noWrap>{reportSubtitle}</Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Preview</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                        {selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''} selected • {rowData.length} records
                      </Typography>
                    </Box>
                    {selectedFields.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Select at least one field to preview.</Typography>
                    ) : (
                      <TableContainer sx={{ height: 300 }}>
                        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                          <TableHead>
                            <TableRow>
                              {selectedFields.map((sf) => (
                                <TableCell
                                  key={sf}
                                  sx={{
                                    fontWeight: 600,
                                    minWidth: 120,
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.7rem'
                                  }}
                                >
                                  {fieldLabel(sf)}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rowData.slice(0, 10).map((row, idx) => (
                              <TableRow key={idx} hover>
                                {selectedFields.map((sf) => (
                                  <TableCell
                                    key={sf}
                                    sx={{
                                      minWidth: 120,
                                      maxWidth: 200,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      fontSize: '0.7rem'
                                    }}
                                    title={row[sf] !== undefined && row[sf] !== null ? String(row[sf]) : ""}
                                  >
                                    {row[sf] !== undefined && row[sf] !== null ? String(row[sf]) : ""}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Report Builder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose what you want to reset:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              color="error"
              onClick={resetEverything}
              sx={{ justifyContent: 'flex-start' }}
            >
              Reset Everything (Logo, Title, Subtitle, Fields)
            </Button>
            <Button
              color="warning"
              onClick={resetFieldsOnly}
              sx={{ justifyContent: 'flex-start' }}
            >
              Reset Fields Only (Keep Logo, Title, Subtitle)
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={saveTemplateDialogOpen} onClose={() => setSaveTemplateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Current as Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Give your template a name. This will save your current field selection, title, subtitle, and logo.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Alumni Contact List, Batch Report 2024"
            sx={{ mt: 1 }}
          />
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Template Preview:
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>Title:</strong> {reportTitle || 'Custom Report'}
            </Typography>
            {reportSubtitle && (
              <Typography variant="body2">
                <strong>Subtitle:</strong> {reportSubtitle}
              </Typography>
            )}
            <Typography variant="body2">
              <strong>Fields:</strong> {selectedFields.length} selected
            </Typography>
            <Typography variant="body2">
              <strong>Logo:</strong> {logoDataUrl ? 'Included' : 'None'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveTemplateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={saveCurrentAsTemplate} 
            variant="contained" 
            disabled={!templateName.trim() || selectedFields.length === 0}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Manager Dialog */}
      <Dialog open={templateManagerOpen} onClose={() => setTemplateManagerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderIcon />
            Saved Templates ({savedTemplates.length})
          </Box>
        </DialogTitle>
        <DialogContent>
          {savedTemplates.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <TemplateIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No Templates Saved
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first template by selecting fields and clicking &quot;Save Template&quot;
              </Typography>
            </Box>
          ) : (
            <List>
              {savedTemplates.map((template) => (
                <ListItem key={template.id} divider>
                  <ListItemButton onClick={() => loadTemplate(template)} sx={{ flexGrow: 1 }}>
                    <ListItemText
                      primary={template.name}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {template.selectedFields?.length || 0} fields • Created: {new Date(template.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Title: {template.reportTitle || 'Custom Report'}
                            {template.reportSubtitle && ` • ${template.reportSubtitle}`}
                            {template.logoDataUrl && ' • Has Logo'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      color="error" 
                      onClick={() => deleteTemplate(template.id)}
                      size="small"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateManagerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Dialog>
  );
};

AdminAlumnusTableDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminAlumnusTableDialog;
